import { TitleCasePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { sortBy } from 'lodash-es';
import { map } from 'rxjs';
import { EXCLUDED_STATES } from '../constants/constants';
import { BankMasterList, BranchMasterList } from '../models/banks.model';
import { ProductCategory, Products, ProductSubCategory, ProductType } from '../models/cdn.model';
import {
    BlockMasterList,
    DistrictMasterList,
    GeoMap,
    PanchayatMasterList,
    StatesMasterList,
    VillageMasterList
} from '../models/geo.model';

const cdnHost = 'https://cdn.lokos.in';
const cdn = `${cdnHost}/lokos-masterdata`;

@Injectable({
    providedIn: 'root'
})
export class CdnService {
    constructor(
        private http: HttpClient,
        readonly titlecase: TitleCasePipe
    ) {}

    /**
     * Get list of all states
     */
    getStates() {
        const url = `${cdn}/statemaster.json`;

        return this.http
            .get<StatesMasterList[]>(url)
            .pipe(map((states) => states.filter(({ state_id }) => !EXCLUDED_STATES.includes(state_id))));
    }

    getStateLists() {
        const url = `${cdn}/statemaster.json`;

        return this.http.get<StatesMasterList[]>(url).pipe(
            map((states): StatesMasterList[] =>
                states.map((state) => ({
                    ...state,
                    state_name_en: this.titlecase.transform(state.state_name_en)
                }))
            )
        );
    }

    getDistrictLists(stateId: string | number) {
        const state = `${stateId}`.padStart(2, '0');
        const url = `${cdn}/state/${state}.json`;

        return this.http.get<DistrictMasterList[]>(url).pipe(
            map((districts): DistrictMasterList[] =>
                districts.map((district) => ({
                    ...district,
                    district_name_en: this.titlecase.transform(district.district_name_en)
                }))
            )
        );
    }

    getBlockLists(stateId: string | number, districtId: string | number) {
        const state = `${stateId}`.padStart(2, '0');
        const url = `${cdn}/state/${state}/district/${districtId}.json`;

        return this.http.get<BlockMasterList[]>(url).pipe(
            map((blocks): BlockMasterList[] =>
                blocks.map((block) => ({
                    ...block,
                    block_name_en: this.titlecase.transform(block.block_name_en)
                }))
            )
        );
    }

    getPanchayatLists(stateId: string | number, districtId: string | number, blockId: string | number) {
        const state = `${stateId}`.padStart(2, '0');
        const url = `${cdn}/state/${state}/district/${districtId}/block/${blockId}.json`;

        return this.http.get<PanchayatMasterList[]>(url).pipe(
            map((data): PanchayatMasterList[] =>
                data.map((item) => ({
                    ...item,
                    panchayat_name_en: this.titlecase.transform(item.panchayat_name_en),
                    villagelist: item.villagelist?.map((village) => ({
                        ...village,
                        villageNameEnglish: this.titlecase.transform(village.villageNameEnglish)
                    }))
                }))
            )
        );
    }

    getVillageLists(
        stateId: string | number,
        districtId: string | number,
        blockId: string | number,
        panchayatId: string | number
    ) {
        const state = `${stateId}`.padStart(2, '0');
        const url = `${cdn}/state/${state}/district/${districtId}/block/${blockId}/panchayat/${panchayatId}.json`;

        return this.http.get<VillageMasterList[]>(url);
    }

    /**
     * Get all districts of a state
     * @param stateId Districts of this state are fetched
     */
    getDistricts(stateId: string | number) {
        const paddedStateId = `${stateId}`.padStart(2, '0');
        const url = `${cdn}/state/${paddedStateId}.json`;

        return this.http.get<DistrictMasterList[]>(url).pipe(map((districts) => sortBy(districts, 'district_name_en')));
    }

    /**
     * Get all blocks of a district
     * @param stateId State ID
     * @param districtId District ID
     */
    getBlocks(stateId: string | number, districtId: string | number) {
        const paddedStateId = `${stateId}`.padStart(2, '0');
        const url = `${cdn}/state/${paddedStateId}/district/${districtId}.json`;

        return this.http.get<BlockMasterList[]>(url).pipe(map((blocks) => sortBy(blocks, 'block_name_en')));
    }

    /**
     * Get all Banks of a district
     * @param stateId State ID
     * @param districtId District ID
     */
    getBanks(stateId: string | number, districtId: string | number) {
        const paddedStateId = `${stateId}`.padStart(2, '0');
        const url = `${cdn}/bank/state/${paddedStateId}/district/${districtId}.json`;

        return this.http.get<BankMasterList[]>(url).pipe(map((banks) => sortBy(banks, 'block_name_en')));
    }

    /**
     * Get all Branches of a district
     * @param stateId State ID
     * @param districtId District ID
     */
    getBranches(stateId: string | number, districtId: string | number) {
        const paddedStateId = `${stateId}`.padStart(2, '0');
        const url = `${cdn}/branch/state/${paddedStateId}/district/${districtId}.json`;

        return this.http.get<BranchMasterList[]>(url).pipe(map((branch) => sortBy(branch, 'block_name_en')));
    }

    /**
     * Get all panchayats of a block
     * @param stateId State ID
     * @param districtId District ID
     * @param blockId Block ID
     */
    getPanchayats(stateId: string | number, districtId: string | number, blockId: string | number) {
        const paddedStateId = `${stateId}`.padStart(2, '0');
        const url = `${cdn}/state/${paddedStateId}/district/${districtId}/block/${blockId}.json`;

        return this.http
            .get<PanchayatMasterList[]>(url)
            .pipe(map((panchayats) => sortBy(panchayats, 'panchayat_name_en')));
    }

    /**
     * Get all villages of a panchayat
     * @param stateId State ID
     * @param districtId District ID
     * @param blockId Block ID
     * @param panchayatdId Panchayat ID
     */
    getVillages(
        stateId: string | number,
        districtId: string | number,
        blockId: string | number,
        panchayatdId: string | number
    ) {
        const paddedStateId = `${stateId}`.padStart(2, '0');
        const url = `${cdn}/state/${paddedStateId}/district/${districtId}/block/${blockId}/panchayat/${panchayatdId}.json`;

        return this.http.get<VillageMasterList[]>(url).pipe(map((villages) => sortBy(villages, 'village_name_en')));
    }

    /**
     * Get all Banks
     */
    getAllBanks() {
        const url = `${cdnHost}/lokos-in/enterprise-loan/prod/banks/BANK_MASTER.json`;

        return this.http.get<BankMasterList[]>(url).pipe(map((banks) => sortBy(banks, 'bank_name')));
    }

    getGeoMap() {
        return this.http.get<GeoMap[]>(`${cdnHost}/keycloak/geo-map.json`);
    }

    getProducts() {
        return this.http.get<Products[]>(`${cdnHost}/gramudyamita/products.json`);
    }

    getProductCategory() {
        return this.http.get<ProductCategory[]>(`${cdnHost}/gramudyamita/productCategory.json`);
    }

    getProductSubCategory() {
        return this.http.get<ProductSubCategory[]>(`${cdnHost}/gramudyamita/productSubCategory.json`);
    }

    getProductType() {
        return this.http.get<ProductType[]>(`${cdnHost}/gramudyamita/productType.json`);
    }
}
